import { useQuery } from '@tanstack/react-query';

import { readLibraryItemFile } from '@/lib/api/generated/api';
import type {
  LibraryFileView,
  LibraryItemKind,
} from '@/lib/api/generated/model';
import { libraryKeys } from '../queryKeys';

/**
 * One file of one library item, read on demand (spec 105 US1).
 *
 * Fetched when the drawer opens rather than with the catalog: most items are
 * never opened, and the catalog is read on every visit to the page.
 *
 * A failure is one line in the drawer, never a thrown error and never a reason
 * to stop somebody installing (FR-026). So the query resolves to `undefined`
 * rather than rejecting, and does not retry: a file that is not there will not
 * be there on the second ask either.
 */
export function useLibraryFile(
  workspaceId: string,
  kind: LibraryItemKind,
  key: string,
  path: string | undefined,
  digest: string,
) {
  const query = useQuery({
    queryKey: libraryKeys.file(workspaceId, kind, key, path ?? '', digest),
    enabled: path !== undefined,
    queryFn: async (): Promise<LibraryFileView | undefined> => {
      if (path === undefined) return undefined;
      const response = await readLibraryItemFile(workspaceId, kind, key, {
        path,
      });
      return response.status === 200 ? response.data : undefined;
    },
    retry: false,
    // Keyed by the item's digest, so what is cached is what the catalog
    // described; a library that has moved on gets a different key.
    staleTime: Infinity,
  });

  return {
    file: query.data,
    isPending: path !== undefined && query.isPending,
    failed: query.isError || (!query.isPending && query.data === undefined),
  };
}
